import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppPinLock } from '@/contexts/AppPinLockContext';
import { useMyInviteToken } from '@/contexts/ChatCryptoBootstrapContext';
import { subscribeChatContacts, type ChatContact } from '@/services/chatContactsStorage';
import { isDefaultChatContactName } from '@/services/chatContactsStorage';
import { useChatUnreadByPeers } from '@/hooks/useChatUnreadByPeers';
import { applyChatUnreadFavicon } from '@/utils/chatFavicon';
import { usePeerInviteProfiles } from '@/hooks/usePeerInviteProfiles';
import type { InviteLookupPublicProfile } from '@/services/chatCryptoFirestore';

export type ChatInboxUnreadContextValue = {
  contacts: ChatContact[];
  contactsReady: boolean;
  unreadByPeer: Record<string, boolean>;
  hasAnyUnread: boolean;
  bumpReadReceipt: () => void;
  peerProfiles: Record<string, InviteLookupPublicProfile>;
  peerProfilesLoadedByToken: Record<string, boolean>;
};

const ChatInboxUnreadContext = createContext<ChatInboxUnreadContextValue | null>(null);

export function ChatInboxUnreadProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? '';
  const myToken = useMyInviteToken();
  const { blocksCalls } = useAppPinLock();
  const { pathname } = useLocation();
  const openChatMatch = matchPath({ path: '/app/chats/:chatId', end: true }, pathname);
  /**
   * While PIN gate (or config load) is up, AppPinLockGate unmounts ChatThread —
   * do not suppress inbox sound/notifications for the URL peer: the thread is not listening.
   * Presence/calls stay off via blocksCalls; message alerts must keep working.
   */
  const suppressSoundPeerToken = blocksCalls ? undefined : openChatMatch?.params.chatId;

  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [contactsReady, setContactsReady] = useState(false);
  const [readReceiptVersion, setReadReceiptVersion] = useState(0);

  useEffect(() => {
    if (!uid) {
      setContacts([]);
      setContactsReady(true);
      return;
    }
    let cancelled = false;
    const unsub = subscribeChatContacts(
      uid,
      (list) => {
        if (!cancelled) {
          setContacts(list);
          setContactsReady(true);
        }
      },
      (err) => console.warn('[ChatInboxUnread] subscribeChatContacts', err)
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid]);

  const contactInviteIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
  const { profiles: peerProfiles, loadedByToken: peerProfilesLoadedByToken } =
    usePeerInviteProfiles(contactInviteIds);

  const getPeerUid = useCallback((peerToken: string) => {
    const c = contacts.find((x) => x.id === peerToken);
    const u = c?.peerUid;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : null;
  }, [contacts]);

  const getPeerNotifyMeta = useCallback((peerToken: string) => {
    const c = contacts.find((x) => x.id === peerToken);
    const profileLoaded = !!peerProfilesLoadedByToken[peerToken];
    const profileName = profileLoaded ? peerProfiles[peerToken]?.displayName?.trim() : '';
    const contactName = typeof c?.name === 'string' ? c.name.trim() : '';
    const title =
      profileName && profileName.length > 0
        ? profileName
        : c && contactName && !isDefaultChatContactName(contactName, c.id)
          ? contactName
          : undefined;
    const iconUrl = profileLoaded ? (peerProfiles[peerToken]?.photoURL ?? null) : null;
    return { title, iconUrl };
  }, [contacts, peerProfiles, peerProfilesLoadedByToken]);

  const unreadByPeer = useChatUnreadByPeers({
    uid,
    myInviteToken: myToken,
    peerTokens: contactInviteIds,
    readReceiptVersion,
    suppressIncomingSoundPeerToken: suppressSoundPeerToken,
    getPeerNotifyMeta,
    getPeerUid,
  });

  const hasAnyUnread = useMemo(
    () => Object.values(unreadByPeer).some(Boolean),
    [unreadByPeer]
  );

  useEffect(() => {
    applyChatUnreadFavicon(hasAnyUnread);
  }, [hasAnyUnread]);

  const bumpReadReceipt = useCallback(() => {
    setReadReceiptVersion((v) => v + 1);
  }, []);

  const value = useMemo(
    () => ({
      contacts,
      contactsReady,
      unreadByPeer,
      hasAnyUnread,
      bumpReadReceipt,
      peerProfiles,
      peerProfilesLoadedByToken,
    }),
    [
      contacts,
      contactsReady,
      unreadByPeer,
      hasAnyUnread,
      bumpReadReceipt,
      peerProfiles,
      peerProfilesLoadedByToken,
    ]
  );

  return (
    <ChatInboxUnreadContext.Provider value={value}>{children}</ChatInboxUnreadContext.Provider>
  );
}

export function useChatInboxUnread(): ChatInboxUnreadContextValue {
  const v = useContext(ChatInboxUnreadContext);
  if (!v) {
    throw new Error('useChatInboxUnread must be used within ChatInboxUnreadProvider');
  }
  return v;
}

export function useOptionalChatInboxUnread(): ChatInboxUnreadContextValue | null {
  return useContext(ChatInboxUnreadContext);
}
